import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  InstanceIntegrityView,
  PlatformIntegrityView,
  VerifyRequestView,
} from "@shared/types/auditIntegrity";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { apiRequest } from "@/lib/api";

/**
 * The audit trail's integrity (docs/research/2026-09-28-design-hipaa-audit.md, "UI"): the
 * platform's view of every chain, one instance's newest result, and "Verify now". Always on since
 * #2501 (owner 2026-11-02): no flag, the server answers `enabled: true`.
 */

export const PLATFORM_INTEGRITY_PATH = "/api/v2/platform/audit/integrity";
export const INSTANCE_INTEGRITY_PATH = "/api/v2/audit/integrity";
export const VERIFY_NOW_PATH = "/api/v2/platform/audit/integrity/verify";

export function usePlatformIntegrity(enabled: boolean) {
  return useQuery<PlatformIntegrityView>({ queryKey: [PLATFORM_INTEGRITY_PATH], enabled });
}

/** The selected instance's result; the key carries the instance, as the header does. */
export function useInstanceIntegrity(enabled: boolean) {
  const { selectedInstance } = useTenantInstance();
  const instanceId = selectedInstance?.id ?? null;
  return useQuery<InstanceIntegrityView>({
    queryKey: [INSTANCE_INTEGRITY_PATH, instanceId],
    queryFn: () => apiRequest<InstanceIntegrityView>(INSTANCE_INTEGRITY_PATH),
    enabled: enabled && instanceId !== null,
  });
}

export function useVerifyNow() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<VerifyRequestView>(VERIFY_NOW_PATH, { method: "POST" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: [PLATFORM_INTEGRITY_PATH] });
    },
  });
}
